import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Modal, Dropdown, Row, Col, Spinner } from 'react-bootstrap';
import { ChevronDown, Filter, Calendar, MapPin, X, Globe, Check, Crop, Users, TrendingUp, Search, Download } from 'react-feather';
import FilterUserDrawer from './FilterUserDrawer';
import { GetGeoDet, getCrops, getFilteredFarmers, downloadFarmersData } from '../lib/api';
import { DecryptFE, EncryptFE } from '../lib/encrypt';
import { Icon } from '@iconify/react/dist/iconify.js';

const FilterUser = () => {
  const [showDistrictModal, setShowDistrictModal] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState('Last Month');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedGender, setSelectedGender] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [ageRange, setAgeRange] = useState([18, 65]);
  const [selectedCrops, setSelectedCrops] = useState([]);
  const [activeTab, setActiveTab] = useState('state');
  const [loading, setLoading] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [cropsData, setCropsData] = useState([]);
  const [farmersData, setFarmersData] = useState([]);
  const [selectedProvinces, setSelectedProvinces] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  const [filteredProvinces, setFilteredProvinces] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Get auth token
  const token = localStorage.getItem('token') || '';

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (geoData && geoData.data) {
      if (selectedCountries.length > 0) {
        const filteredProvinces = geoData.data.ProvinceOrState?.filter(province =>
          selectedCountries.includes(province.CountryID)
        ) || [];
        setFilteredProvinces(filteredProvinces);

        const filteredDistricts = geoData.data.District?.filter(district =>
          selectedCountries.includes(district.CountryID) &&
          (selectedProvinces.length === 0 || selectedProvinces.some(province => province.ProvinceID === district.ProvinceID))
        ) || [];
        setFilteredDistricts(filteredDistricts);
      } else {
        setFilteredProvinces(geoData.data.ProvinceOrState || []);
        setFilteredDistricts(geoData.data.District || []);
      }
    }
  }, [selectedCountries, geoData, selectedProvinces]);

  // Filter districts based on selected provinces when modal is open
  // useEffect(() => {
  //   if (showDistrictModal && geoData?.data?.District) {
  //     if (selectedProvinces.length > 0) {
  //       const filtered = geoData.data.District.filter(district => 
  //         selectedProvinces.some(province => province.ProvinceID === district.ProvinceID)
  //       );
  //       setFilteredDistricts(filtered);
  //     } else if (selectedCountries.length > 0) {
  //       // Filter districts by selected countries only
  //       const filtered = geoData.data.District.filter(district => 
  //         selectedCountries.includes(district.CountryID)
  //       );
  //       setFilteredDistricts(filtered);
  //     } else {
  //       setFilteredDistricts(geoData.data.District || []);
  //     }
  //   }
  // }, [selectedProvinces, showDistrictModal, selectedCountries, geoData]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.country-dropdown')) {
        setCountryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      // Fetch geography data
      const geoResponse = await GetGeoDet();
      setGeoData(geoResponse);

      // Fetch crops data
      const cropsResponse = await getCrops(token);
      setCropsData(cropsResponse);

      // Fetch initial farmers data
      await fetchFilteredFarmers(false, false);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredFarmers = async (isFilterApplied = true, IsRegistrationDateFilterApplied = true) => {
    try {
      setLoading(true);

      const filterParams = {
        IsFilterApplied: isFilterApplied,
        IsRegistrationDateFilterApplied: IsRegistrationDateFilterApplied,
        ResponseType: 'JSON',
        CountryIDs: selectedCountries.join(','),
        ProvinceIDs: selectedProvinces.map(p => p.ProvinceID || p.ID || p.id).filter(Boolean).join(','),
        DistrictIDs: selectedDistricts.map(d => d.DistrictID || d.ID || d.id).filter(Boolean).join(','),
        CropIDs: selectedCrops.join(','),
        Gender: selectedGender.join(','),
        FromAge: ageRange[0],
        ToAge: ageRange[1],
      };

      // Handle date range
      if (selectedDateRange === 'Select Custom Date' && startDate && endDate) {
        filterParams.FromRegistrationDate = startDate;
        filterParams.ToRegistrationDate = endDate;
      } else {
        // Calculate dates based on selected range
        const dateRangeMap = {
          'Last Month': 30,
          'Last 3 Month': 90,
          'Last 6 Month': 180,
          'From Beginning Of Time': null
        };

        if (dateRangeMap[selectedDateRange]) {
          const toDate = new Date().toISOString().split('T')[0];
          const fromDate = new Date();
          fromDate.setDate(fromDate.getDate() - dateRangeMap[selectedDateRange]);
          filterParams.FromRegistrationDate = fromDate.toISOString().split('T')[0];
          filterParams.ToRegistrationDate = toDate;
        }
      }

      const response = await getFilteredFarmers(filterParams, token);

      if (response.status === 'success') {
        // Log the response structure to debug
        console.log("API Response Structure:", response);

        // Get all data arrays from response
        const farmersPersonal = response.data?.FarmersPersonalDetails || [];
        const farmersLocation = response.data?.FarmersLocationDetails || [];
        const farmersFarmingInterests = response.FarmersFarmingInterests || [];
        const farmersCropPhases = response.FarmersCropPhasePeriodDateDetail || [];

        // Combine all data for each farmer
        const combinedData = farmersPersonal.map(personal => {
          const location = farmersLocation.find(
            loc => loc.FarmerID === personal.ID
          ) || {};

          // Get all farming interests for this farmer
          const farmingInterests = farmersFarmingInterests.filter(
            farm => farm.FarmerID === personal.ID
          );

          // Get all crop phases for this farmer
          const cropPhases = farmersCropPhases.filter(
            phase => phase.FarmerID === personal.ID
          );

          return {
            ...personal,
            ...location,
            FarmersFarmingInterests: farmingInterests,
            FarmersCropPhasePeriodDateDetail: cropPhases
          };
        });

        setFarmersData(combinedData);

        // Log combined data for debugging
        console.log("Combined Data:", combinedData);
      }
    } catch (error) {
      console.error('Error fetching filtered farmers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle country selection
  const handleCountryChange = (countryId) => {
    const isSelected = selectedCountries.includes(countryId);
    let newSelectedCountries;

    if (isSelected) {
      newSelectedCountries = selectedCountries.filter(c => c !== countryId);
    } else {
      newSelectedCountries = [...selectedCountries, countryId];
    }

    setSelectedCountries(newSelectedCountries);

    // Clear states and districts if no countries selected
    if (newSelectedCountries.length === 0) {
      setSelectedProvinces([]);
      setSelectedDistricts([]);
    }
  };

  // Handle crop selection
  const handleCropChange = (cropId) => {
    if (selectedCrops.includes(cropId)) {
      setSelectedCrops(selectedCrops.filter(c => c !== cropId));
    } else {
      setSelectedCrops([...selectedCrops, cropId]);
    }
  };

  // Handle gender selection
  const handleGenderChange = (gender) => {
    if (selectedGender.includes(gender)) {
      setSelectedGender(selectedGender.filter(g => g !== gender));
    } else {
      setSelectedGender([gender]); // Only one gender should be selected at a time
    }
  };

  // Handle province selection
  const handleProvinceChange = (province) => {
    const provinceID = province.ProvinceID || province.ID || province.id;
    const isSelected = selectedProvinces.some(p => (p.ProvinceID || p.ID || p.id) === provinceID);

    if (isSelected) {
      setSelectedProvinces(selectedProvinces.filter(p => (p.ProvinceID || p.ID || p.id) !== provinceID));
    } else {
      setSelectedProvinces([...selectedProvinces, province]);
    }
  };

  // Handle district selection
  const handleDistrictChange = (district) => {
    const districtID = district.DistrictID || district.ID || district.id;
    const isSelected = selectedDistricts.some(d => (d.DistrictID || d.ID || d.id) === districtID);

    if (isSelected) {
      setSelectedDistricts(selectedDistricts.filter(d => (d.DistrictID || d.ID || d.id) !== districtID));
    } else {
      setSelectedDistricts([...selectedDistricts, district]);
    }
  };

  // Toggle crop category expansion
  const toggleCategoryExpansion = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedCountries([]);
    setSelectedCrops([]);
    setSelectedGender([]);
    setSelectedDateRange('Last Month');
    setAgeRange([18, 65]);
    setSelectedProvinces([]);
    setSelectedDistricts([]);
    setStartDate('');
    setEndDate('');
  };

  const applyFilters = async () => {
    setCurrentPage(1);
    await fetchFilteredFarmers();
  };

  const handleDownload = async (format) => {
    try {
      setDownloadLoading(true);

      const filterParams = {
        IsFilterApplied: true,
        IsRegistrationDateFilterApplied: false,
        CountryIDs: selectedCountries.join(','),
        ProvinceIDs: selectedProvinces.map(p => p.ProvinceID).join(','),
        DistrictIDs: selectedDistricts.map(d => d.DistrictID).join(','),
        CropIDs: selectedCrops.join(','),
        Gender: selectedGender.join(','),
        FromAge: ageRange[0],
        ToAge: ageRange[1],
        ResponseType: format.toUpperCase()
      };

      if (selectedDateRange === 'Select Custom Date' && startDate && endDate) {
        filterParams.FromRegistrationDate = startDate;
        filterParams.ToRegistrationDate = endDate;
      }

      // Ensure your API call is NOT using responseType: 'blob' 
      // because your server is returning a JSON object, not a direct file stream.
      const response = await downloadFarmersData(filterParams, format, token);

      // 1. Identify which base64 field to use
      const base64String = format === 'pdf' ? response.base64PDF : response.base64Excel;

      if (base64String) {
        // 2. Define the correct Content-Type
        const contentType = format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

        // 3. Convert Base64 to Blob
        // We use a more memory-efficient way to process the string
        const byteCharacters = atob(base64String);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
          const slice = byteCharacters.slice(offset, offset + 512);
          const byteNumbers = new Array(slice.length);
          for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });

        // 4. Trigger the Browser Download
        const fileName = `farmers_${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        throw new Error("No file data received from server");
      }
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      alert(`Error downloading ${format}: ${error.message}`);
    } finally {
      setDownloadLoading(false);
    }
  };

  const StateDistrictModal = () => {
    const provincesToShow = filteredProvinces.filter(province =>
      selectedCountries.length === 0 || selectedCountries.includes(province.CountryID)
    );

    const getCurrentDistrictsToShow = () => {
      if (activeTab === 'district') {
        return filteredDistricts.filter(district =>
          selectedCountries.length === 0 || selectedCountries.includes(district.CountryID)
        );
      }
      return filteredDistricts;
    };

    const districtsToShow = getCurrentDistrictsToShow();

    const handleSelectAll = (type) => {
      if (type === 'state') {
        if (selectedProvinces.length === provincesToShow.length) {
          setSelectedProvinces([]);
        } else {
          setSelectedProvinces([...provincesToShow]);
        }
      } else {
        if (selectedDistricts.length === districtsToShow.length) {
          setSelectedDistricts([]);
        } else {
          setSelectedDistricts([...districtsToShow]);
        }
      }
    };

    const clearModalSelections = () => {
      if (activeTab === 'state') {
        setSelectedProvinces([]);
      } else {
        setSelectedDistricts([]);
      }
    };

    return (
      <Modal show={showDistrictModal} onHide={() => setShowDistrictModal(false)} size="md">
        <Modal.Header closeButton style={{ borderBottom: '2px solid #C8E6C9', padding: '16px 24px', }}>
          <Modal.Title style={{ color: '#1B5E20', fontWeight: '500', fontSize: '18px' }}>
            Filter By State & District
            {selectedCountries.length > 0 && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Filtering for selected countries
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px' }}>
          <div className="mb-4">
            <div className="d-flex" style={{ borderBottom: '1px solid #e0e0e0' }}>
              <div
                className={`px-3 py-2 me-4 cursor-pointer ${activeTab === 'state' ? 'border-bottom border-primary' : ''}`}
                onClick={() => setActiveTab('state')}

                style={{
                  fontSize: '14px',
                  color: activeTab === 'state' ? '#1B5E20' : '#666',
                  fontWeight: activeTab === 'state' ? '600' : 'normal',
                  backgroundColor: activeTab === 'state' ? '#F1F8E9' : 'transparent',
                  borderRadius: '4px 4px 0 0',
                  border: 'none',
                  borderBottom: activeTab === 'state' ? '4px solid #1B5E20' : '4px solid transparent',
                  marginRight: '10px',
                  transition: 'all 0.1s ease-in-out',
                  lineHeight: '1.2'
                }}
              >
                State
              </div>
              <div
                className={`px-3 py-2 cursor-pointer ${activeTab === 'district' ? 'border-bottom border-primary' : ''}`}
                onClick={() => setActiveTab('district')}
                style={{
                  fontSize: '14px',
                  color: activeTab === 'district' ? '#1B5E20' : '#666',
                  fontWeight: activeTab === 'district' ? '600' : 'normal',
                  backgroundColor: activeTab === 'district' ? '#F1F8E9' : 'transparent',
                  borderRadius: '4px 4px 0 0',
                  border: 'none',
                  borderBottom: activeTab === 'district' ? '4px solid #1B5E20' : '4px solid transparent',
                  marginRight: '10px',
                  transition: 'all 0.1s ease-in-out',
                  lineHeight: '1.2'
                }}
              >
                District
              </div>
            </div>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'state' ? (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  variant="link"
                  size="sm"
                  style={{
                    color: '#1B5E20',
                    textDecoration: 'none',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '0'
                  }}
                  onClick={() => handleSelectAll('state')}
                >
                  {/* {selectedProvinces.length === provincesToShow.length ? 'Unselect All' : 'Select All'} */}
                </Button>
              </div>
              {provincesToShow.length === 0 ? (
                <div className="text-center py-4" style={{ color: '#666' }}>
                  {selectedCountries.length > 0
                    ? 'No states available for selected countries'
                    : 'No states available'}
                </div>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '8px' }}>
                  {provincesToShow.map((province) => {
                    // 1. Determine the correct Unique ID (Check your console to be sure!)
                    const uniqueID = province.ProvinceID || province.ID || province.id;

                    // 2. Check if selected using the safe ID
                    const isSelected = selectedProvinces.some(p =>
                      (p.ProvinceID || p.ID || p.id) === uniqueID
                    );

                    return (
                      <div
                        key={uniqueID} // Use the safe ID
                        className="d-flex align-items-center py-2 px-2 mb-1"
                        style={{
                          backgroundColor: isSelected ? '#F1F8E9' : 'transparent',
                          borderRadius: '4px',
                          minHeight: '36px',
                          cursor: 'pointer'
                        }}
                        // 3. ONLY handle click here on the container
                        onClick={() => handleProvinceChange(province)}
                      >
                        <div className="me-3" style={{ minWidth: '20px' }}>
                          <Form.Check
                            type="checkbox"
                            id={`state-${uniqueID}`} // Unique ID for accessibility
                            checked={isSelected}
                            // 4. Make checkbox 'readonly' to React - let the Div onClick handle the logic
                            // This prevents double-firing events
                            onChange={() => { }}
                            style={{ margin: 0, pointerEvents: 'none' }} // Pass clicks through to the Div
                          />
                        </div>

                        {/* 5. Remove 'htmlFor' to prevent the label from triggering the checkbox independently */}
                        <label
                          style={{
                            color: '#1B5E20',
                            fontSize: '13px',
                            cursor: 'pointer',
                            margin: 0,
                            flex: 1,
                            fontWeight: isSelected ? '500' : '400',
                            pointerEvents: 'none' // Pass clicks through to the Div
                          }}
                        >
                          {province.ProvinceName}
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  variant="link"
                  size="sm"
                  style={{
                    color: '#1B5E20',
                    textDecoration: 'none',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '0'
                  }}
                  onClick={() => handleSelectAll('district')}
                >
                  {/* {selectedDistricts.length === districtsToShow.length ? 'Unselect All' : 'Select All'} */}
                </Button>
              </div>
              {districtsToShow.length === 0 ? (
                <div className="text-center py-4" style={{ color: '#666' }}>
                  {selectedCountries.length > 0
                    ? 'No districts available for selected countries'
                    : 'No districts available'}
                </div>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '8px' }}>
                  {districtsToShow.map((district) => {
                    const uniqueID = district.DistrictID || district.ID || district.id;

                    const isSelected = selectedDistricts.some(d =>
                      (d.DistrictID || d.ID || d.id) === uniqueID
                    );

                    return (
                      <div
                        key={uniqueID}
                        className="d-flex align-items-center py-2 px-2 mb-1"
                        style={{
                          backgroundColor: isSelected ? '#F1F8E9' : 'transparent',
                          borderRadius: '4px',
                          minHeight: '36px',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleDistrictChange(district)}
                      >
                        <div className="me-3" style={{ minWidth: '20px' }}>
                          <Form.Check
                            type="checkbox"
                            id={`district-${uniqueID}`}
                            checked={isSelected}
                            onChange={() => { }}
                            style={{ margin: 0, pointerEvents: 'none' }}
                          />
                        </div>
                        <label
                          style={{
                            color: '#1B5E20',
                            fontSize: '13px',
                            cursor: 'pointer',
                            margin: 0,
                            flex: 1,
                            fontWeight: isSelected ? '500' : '400',
                            pointerEvents: 'none'
                          }}
                        >
                          {district.DistrictName}
                          <div style={{ fontSize: '11px', color: '#666' }}>
                            {district.ProvinceName}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0' }}>
          <div className="d-flex justify-content-between w-100 align-items-center">
            <Button
              variant="outline-secondary"
              onClick={clearModalSelections}
              style={{
                borderColor: '#C8E6C9',
                color: '#666',
                fontSize: '14px',
                padding: '6px 16px',
                fontWeight: '500'
              }}
            >
              Clear All
            </Button>
            <div>
              <Button
                variant="outline-secondary"
                onClick={() => setShowDistrictModal(false)}
                style={{
                  borderColor: '#C8E6C9',
                  color: '#666',
                  marginRight: '12px',
                  fontSize: '14px',
                  padding: '6px 16px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </Button>
              <Button
                style={{
                  backgroundColor: '#1B5E20',
                  borderColor: '#1B5E20',
                  fontSize: '14px',
                  padding: '6px 20px',
                  fontWeight: '500'
                }}
                onClick={() => {
                  setShowDistrictModal(false);
                  applyFilters();
                }}
              >
                Apply Filter
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  // Group crops by category
  const groupedCrops = cropsData.reduce((acc, crop) => {
    if (!acc[crop.Category]) {
      acc[crop.Category] = [];
    }
    acc[crop.Category].push(crop);
    return acc;
  }, {});

  // Handle pagination
  const handlePageChange = (page) => {
    if (page !== '...') {
      setCurrentPage(page);
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  // Gender encryption mapping
  const genderMapping = {
    'Male': 'vtXtEBWaS2NWCijTDW13dw==',
    'Female': 'joyXnpjzEc0yRhHUVRQ4Cw==',
    'Other': '3V76nhuJOR55tAtnpFeJ2A=='
  };

  // Filter and paginate data
  const filteredData = farmersData.filter(user => {
    if (!searchTerm) return true;
    const fullName = `${DecryptFE(user.GivenName || '')} ${DecryptFE(user.FamilyName || '')}`.toLowerCase();
    const country = (user.Country || '').toLowerCase();
    const state = (user.ProvinceOrState || '').toLowerCase();
    const district = (user.District || '').toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    return fullName.includes(searchLower) || country.includes(searchLower) || state.includes(searchLower) || district.includes(searchLower);
  });

  const totalFilteredUsers = filteredData.length;
  const totalPages = Math.ceil(totalFilteredUsers / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const slicedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const generatePageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= Math.min(4, totalPages); i++) {
      pages.push(i);
    }
    if (totalPages > 5) {
      pages.push('...');
      pages.push(totalPages);
    } else if (totalPages > 4) {
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="container-fluid py-3">
      {/* Filters Section */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4" style={{ paddingBottom: '30px' }}>
        <div className="d-flex align-items-center">
          <div className="mb-0 d-flex align-items-center" style={{ color: '#1B5E20', fontWeight: 'normal', fontSize: '30px' }}>
            <Filter size={20} className="me-2" style={{ color: '#1B5E20' }} />
            Filter User
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant="link"
            onClick={clearAllFilters}
            style={{ color: '#000', textDecoration: 'none', fontSize: '14px', fontWeight: '500', padding: '5px 15px', borderRadius: '4px', border: '1px solid #ccc', height: '40px' }}
          >
            Clear All
          </Button>

          {/* Download Buttons */}
          <Dropdown>
            <Dropdown.Toggle
              variant="outline-success"
              style={{
                borderColor: '#1B5E20',
                // color: '#1B5E20',
                fontSize: '14px',
                fontWeight: '500',
                padding: '5px 15px',
                height: '40px',
                display: 'flex',
                alignItems: 'center'
              }}
              disabled={downloadLoading}
            >
              {downloadLoading ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <Download size={16} className="me-2" />
              )}
              Download
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleDownload('pdf')} disabled={downloadLoading}>
                Download as PDF
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleDownload('excel')} disabled={downloadLoading}>
                Download as Excel
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Button
            onClick={applyFilters}
            style={{
              backgroundColor: '#1B5E20',
              borderColor: '#1B5E20',
              fontWeight: '600',
              fontSize: '14px',
              padding: '5px 20px',
              height: '40px'
            }}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Loading...
              </>
            ) : 'Apply Filter'}
          </Button>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="p-3">
          <Row className="g-3 d-flex align-items-start">
            {/* Country Selection */}
            <Col xs={12} md={6} lg={4} xl={2} className="mb-3 mb-xl-0">
              <div className="mb-2 d-flex align-items-center">
                <Globe size={16} className="me-2" style={{ color: '#1B5E20' }} />
                <span className="fw-medium">Country</span>
              </div>

              <div className="position-relative country-dropdown">
                <div
                  className="border rounded p-2 d-flex align-items-center justify-content-between"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: 'white',
                    minHeight: '40px'
                  }}
                  onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                >
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    {selectedCountries.length === 0
                      ? 'Select Countries'
                      : `${selectedCountries.length} selected`
                    }
                  </span>
                  <ChevronDown
                    size={14}
                    style={{
                      transform: countryDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      color: '#1B5E20'
                    }}
                  />
                </div>

                {countryDropdownOpen && (
                  <div className="border rounded mt-1 p-2 position-absolute w-100" style={{
                    backgroundColor: 'white',
                    zIndex: 1000,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                    {geoData?.data?.Country?.map((country) => (
                      <div
                        key={country.ID}
                        className="px-2 py-2 d-flex align-items-center hover-bg-light rounded"
                        onClick={() => handleCountryChange(country.ID)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: selectedCountries.includes(country.ID) ? '#E8F5E8' : 'transparent',
                          transition: 'background-color 0.2s',
                          margin: '2px 0'
                        }}
                      >
                        {country.ImageOfFlag ? (
                          <img
                            src={`https://api.soksan-dss.com/uploads/flag/${country.ImageOfFlag}`}
                            alt={country.CountryName}
                            style={{ width: '20px', height: '15px', marginRight: '8px' }}
                          />
                        ) : (
                          <span role="img" aria-label={country.CountryName} className="me-2" style={{ fontSize: '18px' }}>
                            {country.CountryName === 'Cambodia' ? '🇰🇭' :
                              country.CountryName === 'Laos' ? '🇱🇦' :
                                country.CountryName === 'Myanmar' ? '🇲🇲' :
                                  country.CountryName === 'Vietnam' ? '🇻🇳' : '🌐'}
                          </span>
                        )}
                        <span className="flex-grow-1" style={{ fontSize: '14px' }}>{country.CountryName}</span>
                        {selectedCountries.includes(country.ID) && <Check size={16} className="ms-auto" color="#1B5E20" />}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                variant="success"
                className="w-100 mt-72"
                onClick={() => setShowDistrictModal(true)}
                style={{
                  backgroundColor: '#1B5E20',
                  borderColor: '#1B5E20',
                  fontSize: '14px',
                  fontWeight: '500',
                  padding: '4px 12px'
                }}
              >
                Filter By State & District
              </Button>
            </Col>

            {/* Date Filter */}
            <Col xs={12} md={6} lg={4} xl={4} className="mb-3 mb-xl-0">
              <div className="mb-2 d-flex align-items-center">
                <Calendar size={16} className="me-2" style={{ color: '#1B5E20' }} />
                <span className="fw-medium">Date</span>
              </div>

              <div className="d-flex flex-wrap gap-1 mb-3">
                {['Last Month', 'Last 3 Month', 'Last 6 Month', 'From Beginning Of Time', 'Select Custom Date'].map((range, index) => (
                  <Button
                    key={index}
                    onClick={() => setSelectedDateRange(range)}
                    className="py-1 px-3"
                    style={{
                      fontSize: '13px',
                      fontWeight: '500',
                      backgroundColor: selectedDateRange === range ? '#1B5E20' : 'transparent',
                      borderColor: selectedDateRange === range ? '#1B5E20' : '#ccc',
                      color: selectedDateRange === range ? 'white' : '#666',
                      borderRadius: '15px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {range}
                  </Button>
                ))}
              </div>

              <div className="mb-2">
                <div className="d-flex flex-column flex-md-row align-items-center gap-2">
                  <div className="w-100">
                    <Form.Label className="text-muted small mb-1">Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      style={{ fontSize: '14px' }}
                      className="w-100"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      disabled={selectedDateRange !== 'Select Custom Date'}
                    />
                  </div>
                  <span className="text-muted d-none d-md-block">-</span>
                  <div className="w-100">
                    <Form.Label className="text-muted small mb-1">End Date</Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      style={{ fontSize: '14px' }}
                      className="w-100"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      disabled={selectedDateRange !== 'Select Custom Date'}
                    />
                  </div>
                </div>
              </div>
            </Col>

            {/* Crop Selection */}
            <Col xs={12} md={6} lg={4} xl={2} className="mb-3 mb-xl-0">
              <div className="mb-2 d-flex align-items-center gap-2">
                <img src='assets/images/asset/GroupCrop.svg' />
                <span className="">Crop</span>
              </div>

              <div className="border rounded p-2" style={{
                maxHeight: '280px',
                overflowY: 'auto',
                backgroundColor: 'white'
              }}>
                {Object.entries(groupedCrops).map(([category, crops]) => (
                  <div key={category} className="mb-3">
                    <div
                      className="d-flex align-items-center justify-content-between p-2 rounded mb-2"
                      style={{
                        backgroundColor: '#E8F5E8',
                        color: '#1B5E20',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                      onClick={() => toggleCategoryExpansion(category)}
                    >
                      <span>{category}</span>
                      <ChevronDown
                        size={14}
                        style={{
                          transform: expandedCategories[category] ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }}
                      />
                    </div>

                    {expandedCategories[category] && (
                      <div className="ms-3">
                        {crops.map((crop) => (
                          <Form.Check
                            key={crop.ID}
                            type="checkbox"
                            id={`crop-check-${crop.ID}`}
                            label={crop.CropName}
                            checked={selectedCrops.includes(crop.ID)}
                            onChange={() => handleCropChange(crop.ID)}
                            className="mb-2 py-1"
                            style={{ fontSize: '14px' }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Col>

            {/* Gender Filter */}
            <Col xs={8} md={4} lg={3} xl={2} className="mb-3 mb-xl-0">
              <div className="mb-2 d-flex align-items-center">
                <Users size={16} className="me-2" style={{ color: '#1B5E20' }} />
                <span className="fw-medium">Gender</span>
              </div>

              <div className="d-flex flex-column gap-2">
                {['Male', 'Female', 'Other'].map((gender, index) => (
                  <Button
                    key={index}
                    onClick={() => handleGenderChange(genderMapping[gender])}
                    className="py-2"
                    style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      textAlign: 'start',
                      backgroundColor: selectedGender.includes(genderMapping[gender]) ? '#126B3A' : 'transparent',
                      borderColor: selectedGender.includes(genderMapping[gender]) ? '#126B3A' : '#ccc',
                      color: selectedGender.includes(genderMapping[gender]) ? 'white' : '#000',
                      borderRadius: '6px',
                      transition: 'all 0.2s'
                    }}
                  >
                    {gender}
                  </Button>
                ))}
              </div>
            </Col>

            {/* Age Range */}
            <Col xs={12} md={6} lg={4} xl={2} className="mb-3 mb-xl-0">
              <div className="mb-2 d-flex align-items-center">
                <TrendingUp size={16} className="me-2" style={{ color: '#1B5E20' }} />
                <span className="fw-medium">Age</span>
              </div>

              <div className="border rounded p-3">
                <div className="d-flex justify-content-between mb-2">
                  <span className="small fw-medium">Min Age: {ageRange[0]}</span>
                  <span className="small fw-medium">Max Age: {ageRange[1]}</span>
                </div>

                <div className="mb-3">
                  <label className="small fw-medium">Minimum Age</label>
                  <Form.Range
                    min={10}
                    max={100}
                    value={ageRange[0]}
                    onChange={(e) => {
                      const newMin = parseInt(e.target.value);
                      setAgeRange([newMin, Math.max(newMin, ageRange[1])]);
                    }}
                    className="w-100"
                    style={{ accentColor: '#1B5E20' }}
                  />
                </div>

                <div className="mb-3">
                  <label className="small fw-medium">Maximum Age</label>
                  <Form.Range
                    min={10}
                    max={100}
                    value={ageRange[1]}
                    onChange={(e) => {
                      const newMax = parseInt(e.target.value);
                      setAgeRange([Math.min(ageRange[0], newMax), newMax]);
                    }}
                    className="w-100"
                    style={{ accentColor: '#1B5E20' }}
                  />
                </div>

                <div className="d-flex justify-content-center mt-3">
                  <span className="fw-bold text-nowrap" style={{ color: '#1B5E20', fontSize: '16px' }}>
                    {ageRange[0]} - {ageRange[1]}
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-center mb-4 border-bottom pb-3 gap-3">

            <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100">

              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <div className="mb-0" style={{ color: '#1B5E20', fontWeight: '500', fontSize: '20px', whiteSpace: 'nowrap' }}>
                  User Result
                </div>

                <div
                  className='rounded-4'
                  style={{
                    color: 'black',
                    padding: '2px 9px',
                    fontSize: '14px',
                    fontWeight: 'normal',
                    backgroundColor: '#E8F5E8',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {totalFilteredUsers} User{totalFilteredUsers !== 1 ? 's' : ''}
                </div>
              </div>

              <div className="input-group input-group-sm ms-md-4 w-100" style={{ maxWidth: '600px', height: '40px' }}>
                <Form.Control
                  type="text"
                  placeholder="Search users..."
                  className="border-end-0"
                  style={{ fontSize: '15px' }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="input-group-text bg-white" style={{ borderColor: '#ced4da' }}>
                  <Search size={16} color="#666" />
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center flex-shrink-0">
              <div className="d-flex align-items-center" style={{ fontSize: '14px', color: '#000' }}>
                <span className="me-2 fw-medium">Show</span>
                <Form.Select
                  size="sm"
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(e.target.value)}
                  style={{
                    width: '70px',
                    fontSize: '14px',
                    height: '40px',
                    borderRadius: '8px'
                  }}
                  className="fw-medium"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </Form.Select>
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="success" />
              <p className="mt-2">Loading users...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0 5px' }}>
                  <thead style={{ backgroundColor: '#51AE3B21', border: '1px solid #E8F5E9' }}>
                    <tr>
                      <th style={{ color: '#1B5E20', fontWeight: '600', borderTop: 'none', padding: '12px 10px' }}>User Name</th>
                      <th style={{ color: '#1B5E20', fontWeight: '600', borderTop: 'none', padding: '12px 10px' }}>Date Of Registration</th>
                      <th style={{ color: '#1B5E20', fontWeight: '600', borderTop: 'none', padding: '12px 10px' }}>Country</th>
                      <th style={{ color: '#1B5E20', fontWeight: '600', borderTop: 'none', padding: '12px 10px' }}>State</th>
                      <th style={{ color: '#1B5E20', fontWeight: '600', borderTop: 'none', padding: '12px 10px' }}>District</th>
                      <th style={{ color: '#1B5E20', fontWeight: '600', borderTop: 'none', padding: '12px 10px' }}>Commune</th>
                      <th style={{ color: '#1B5E20', fontWeight: '600', borderTop: 'none', padding: '12px 10px' }}>Village</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slicedData.map((user, index) => (
                      <tr
                        key={user.ID || index}
                        onClick={() => setSelectedUser(user)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: selectedUser?.ID === user.ID ? '#F1F8E9' : 'transparent',
                          borderBottom: '1px solid #eee'
                        }}
                      >
                        <td style={{ verticalAlign: 'middle', fontWeight: '500', color: '#333', padding: '10px' }}>
                          {user.GivenName || user.FamilyName ? `${DecryptFE(user.GivenName)} ${DecryptFE(user.FamilyName)}` : 'N/A'}
                        </td>
                        <td style={{ verticalAlign: 'middle', color: '#666', padding: '10px', fontSize: '14px' }}>
                          {new Date(user.CreatedOn).toLocaleDateString()}
                        </td>
                        <td style={{ verticalAlign: 'middle', fontSize: '14px', padding: '10px' }}>
                          {user.CountryImageName ? (
                            <img
                              src={`https://api.soksan-dss.com/uploads/flag/${user.CountryImageName}`}
                              alt={user.CountryImageName}
                              style={{ width: '20px', height: '15px', marginRight: '8px' }}
                            />
                          ) : (
                            <span role="img" aria-label={user.CountryImageName} className="me-2" style={{ fontSize: '18px' }}>
                              <Icon icon="streamline-ultimate-color:network-pin" />
                            </span>
                          )}
                        </td>
                        <td style={{ verticalAlign: 'middle', color: '#666', padding: '10px', fontSize: '14px' }}>
                          {user.ProvinceOrState || 'N/A'}
                        </td>
                        <td style={{ verticalAlign: 'middle', color: '#666', padding: '10px', fontSize: '14px' }}>
                          {user.District || 'N/A'}
                        </td>
                        <td style={{ verticalAlign: 'middle', color: '#666', padding: '10px', fontSize: '14px' }}>
                          {user.Commune || 'N/A'}
                        </td>
                        <td style={{ verticalAlign: 'middle', color: '#666', padding: '10px', fontSize: '14px' }}>
                          {user.Village || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 pt-3 border-top gap-3">
                {/* Left Side: Result Count - Centered on mobile, Left-aligned on desktop */}
                <div className="text-muted text-center text-md-start" style={{ fontSize: '14px' }}>
                  Showing <strong>{slicedData.length}</strong> Result Out <strong>{totalFilteredUsers}</strong> Users
                </div>

                {/* Right Side: Pagination Controls */}
                <div className="d-flex align-items-center justify-content-center">
                  {/* Previous Button - Smaller padding on mobile */}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="px-2 px-sm-3 me-1 me-sm-2"
                    style={{
                      borderColor: '#C8E6C9',
                      color: currentPage === 1 ? '#999' : '#666',
                      fontSize: '14px'
                    }}
                  >
                    &lt; <span className="d-none d-sm-inline">Previous</span>
                  </Button>

                  {/* Page Numbers - Hidden on extra small devices to prevent overflow */}
                  <div className="d-none d-sm-flex">
                    {generatePageNumbers().map((page, index) => (
                      <Button
                        key={index}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        disabled={page === '...'}
                        className="mx-1"
                        style={{
                          minWidth: '32px',
                          padding: '6px 12px',
                          backgroundColor: page === currentPage ? '#1B5E20' : 'transparent',
                          borderColor: page === currentPage ? '#1B5E20' : '#C8E6C9',
                          color: page === currentPage ? '#fff' : '#666',
                          fontWeight: page === currentPage ? 'bold' : 'normal',
                          fontSize: '14px'
                        }}
                      >
                        {page}
                      </Button>
                    ))}
                  </div>

                  {/* Mobile Page Indicator - Only shows on tiny screens instead of numbers */}
                  <span className="d-sm-none mx-2 text-muted" style={{ fontSize: '14px' }}>
                    {currentPage} / {totalPages}
                  </span>

                  {/* Next Button */}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="px-2 px-sm-3 ms-1 ms-sm-2"
                    style={{
                      borderColor: '#C8E6C9',
                      color: currentPage === totalPages ? '#999' : '#666',
                      fontSize: '14px'
                    }}
                  >
                    <span className="d-none d-sm-inline">Next</span> &gt;
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <StateDistrictModal />

      {/* User Detail Drawer */}
      {selectedUser && (
        <FilterUserDrawer
          selectedUser={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
};

export default FilterUser;